// External libraries
import PropTypes from 'prop-types';
import { get, noop } from 'lodash';
import React, { PureComponent } from 'react';
import { findDOMNode } from 'react-dom';
import { Provider } from 'react-redux';
import styled, { css, ThemeProvider, withTheme } from 'styled-components';

// Zocdoc libraries
import { grey25 } from '@zocdoc/branding/colors';
import { getLogo } from '@zocdoc/branding/brands/helpers';
import { filterToValidAttributes } from '@zocdoc-frontend-common/utils-dom-property';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import { MetricsClickable, Metrics } from '@zocdoc/frontend-metrics';
import { breakpoint } from '@zocdoc-frontend-common/styles-grid';
import { Link } from '@zocdoc-frontend-common/components-link';
import { getLogoSizingCSSAtBreakpoint } from '@zocdoc-frontend-common/utils-themes/helpers';
import CoronaVirusAlertBanner from '@zocdoc-frontend-common/components-alertbanner/CoronaVirusAlertBanner';
import isMobileOrTabletBreakpoint from '@zocdoc-frontend-common/utils-deviceutils/isMobileOrTabletBreakpoint';
import { isZocdocBrand } from '@zocdoc-frontend-common/utils-react';
import PrivacyUpdateBanner from '@zocdoc-frontend-common/components-privacyupdatebanner';
import LockableLink from '@zocdoc-frontend-common/components-lockable-link';
import PaddedContainer, {
    PAGE_X_AXIS_PADDING_BY_BREAKPOINT,
} from '@zocdoc-frontend-common/components-padded-container';
import MobileMenu from '@zocdoc-frontend-common/components-mobile-menu';
import PoweredByZocdoc from '@zocdoc-frontend-common/components-powered-by-zocdoc-badge';
import NotificationMenu from '@zocdoc-frontend-common/components-notification-menu';
import UsernameDropdown from '@zocdoc-frontend-common/components-username-dropdown';
import SignInDropdown from '@zocdoc-frontend-common/components-sign-in-dropdown';
import {
    LOGIN_STATE,
    USER_TYPE,
} from '@zocdoc-frontend-common/state-user-account/constants';
import { DesktopUserNavLinks } from '@zocdoc-frontend-common/components-username-dropdown/constants';

// This package
import {
    UPDATE_PRIVACY_POLICY_BANNER_FLAG,
    VARIANT_HIDDEN,
    VARIANT_SHOWN,
} from './experiments';
import { HEADER_Y_AXIS_PADDING } from './constants';
import StaticSmallBlobLogoImage from './assets/zocdoc-small-blob-logo.svg';
import StaticLargeBlobLogoImage from './assets/zocdoc-large-blob-logo.svg';

const PATIENT_HOME_V3_BACKGROUND_COLOR = '#D8E9FD';

class Header extends PureComponent {
    static propTypes = {
        ab: PropTypes.object,
        defaultOpenCollapsibleLogin: PropTypes.bool,
        defaultShowLoginModal: PropTypes.bool,
        dropdownMenuDataTest: PropTypes.string,
        fakeImportLoginForm: PropTypes.object,
        fakeImportLoginModal: PropTypes.object,
        hideUserArea: PropTypes.bool, // hide content on the right side of the header
        // any React-renderable content
        mobileMenuContent: PropTypes.any,
        mobileMenuTriggerDataTest: PropTypes.string,

        facebookLoginUrl: PropTypes.string,
        googleLoginUrl: PropTypes.string,
        appleLoginUrl: PropTypes.string,
        loginReturnUrl: PropTypes.string,

        onCTALinkClick: PropTypes.func,
        onCustomLinkClick: PropTypes.func,
        onLogoClick: PropTypes.func,
        onMobileMenuLinkClick: PropTypes.func,
        onNavigationLinkClick: PropTypes.func,
        onNotificationDropdownClick: PropTypes.func,
        onNotificationClick: PropTypes.func,
        onSeeAllNotificationsClick: PropTypes.func,
        onSignInDropdownClick: PropTypes.func,
        onSignInDropdownLinkClick: PropTypes.func,
        onUpcomingAppointmentClick: PropTypes.func,
        onUsernameDropdownClick: PropTypes.func,
        onUsernameDropdownLinkClick: PropTypes.func,
        recordRememberMeAssignment: PropTypes.func,

        showLoginRememberMe: PropTypes.bool,
        showNavigationLinks: PropTypes.bool,
        showSubPatientDropdownLink: PropTypes.bool,
        splitLoginAndSignupLink: PropTypes.bool,

        signInDropdownText: PropTypes.string,
        // created and injected in /src/Layout/Header/index.js
        store: PropTypes.object.isRequired,

        theme: PropTypes.shape({
            header: PropTypes.shape({
                ctaLink: PropTypes.shape({
                    uri: PropTypes.string,
                    text: PropTypes.string,
                    title: PropTypes.string,
                }),

                logo: PropTypes.shape({
                    linkUri: PropTypes.string,
                }),

                rightContent: PropTypes.arrayOf(
                    PropTypes.shape({
                        uri: PropTypes.string,
                        text: PropTypes.string,
                        title: PropTypes.string,
                    })
                ),
                showLoginDropdown: PropTypes.bool,
                showPoweredByZocdoc: PropTypes.bool,
                showPoweredByZocdocAsLink: PropTypes.bool,
            }),
        }),
        trackSignInLinkClickConversion: PropTypes.func,
        transparentOnDesktop: PropTypes.bool,
        useAnimatedLogo: PropTypes.bool,
        useLoginModal: PropTypes.bool,
        usePatientHomeView: PropTypes.bool,
        useStaticSmallBlobLogo: PropTypes.bool,
        useSignInDropdown: PropTypes.bool,
        user: PropTypes.object,
        wellGuideLinkVariant: PropTypes.string,
        __windowLocationPathName__: PropTypes.string,
        /**
         * Being used for showing both visit reason and address when collapsed
         * @link https://jira.zocdoc.local/confluence/display/DEV/FE%3A+Redesigned+mobile+header
         */
        inlineChildren: PropTypes.node,
        shouldHideLogoForMobile: PropTypes.bool,
        shouldHideLogoForDesktop: PropTypes.bool,
        transparentOnAll: PropTypes.bool,

        onClickCoronaVirusBanner: PropTypes.func,

        // Search Bar v2 experiment
        shouldShowNewSearchHeader: PropTypes.bool,
        numberOfNavLinksToShowInUserDropdown: PropTypes.number,
    };

    static defaultProps = {
        defaultOpenCollapsibleLogin: false,
        defaultShowLoginModal: false,
        facebookLoginUrl: '',
        googleLoginUrl: '',
        appleLoginUrl: '',
        hideUserArea: false,
        loginReturnUrl: '',
        onCTALinkClick: noop,
        onCustomLinkClick: noop,
        onLogoClick: noop,
        onMobileMenuLinkClick: noop,
        onNavigationLinkClick: ev => ev,
        onNotificationClick: noop,
        onNotificationDropdownClick: noop,
        onSeeAllNotificationsClick: noop,
        onUpcomingAppointmentClick: noop,
        onUsernameDropdownClick: noop,
        onUsernameDropdownLinkClick: noop,
        onSignInDropdownClick: noop,
        onSignInDropdownLinkClick: noop,
        recordRememberMeAssignment: noop,
        showLoginRememberMe: false,
        usePatientHomeV3Variant: false,
        splitLoginAndSignupLink: false,
        trackSignInLinkClickConversion: noop,
        transparentOnDesktop: false,
        useAnimatedLogo: false,
        useLoginModal: false,
        usePatientHomeView: false,
        useSignInDropdown: false,
        user: {},
        inlineChildren: undefined,
        shouldHideLogoForMobile: false,
        shouldHideLogoForDesktop: false,
        transparentOnAll: false,

        //Corona Virus:
        shouldShowCoronaVirusBanner: false,
        onClickCoronaVirusBanner: noop,

        // Search Bar v2 experiment
        shouldShowNewSearchHeader: false,
        numberOfNavLinksToShowInUserDropdown: 0,
    };

    animationRendered = false;

    componentDidMount() {
        this.maybeRenderAnimation();
        window.addEventListener('resize', this.maybeRenderAnimation);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.maybeRenderAnimation);
    }

    render() {
        const {
            children,
            defaultOpenCollapsibleLogin,
            facebookLoginUrl,
            fakeImportLoginForm,
            googleLoginUrl,
            appleLoginUrl,
            loginReturnUrl,
            mobileMenuContent,
            mobileMenuTriggerDataTest,
            onLogoClick,
            onMobileMenuLinkClick,
            recordRememberMeAssignment,
            rightAlignMobileMenu,
            showLoginRememberMe,
            splitLoginAndSignupLink,
            transparentOnDesktop,
            transparentOnAll,
            useLoginModal,
            usePatientHomeV3Variant,
            usePatientHomeView,
            wellGuideLinkVariant,
            inlineChildren,
            hideUserArea,
            ...props
        } = this.props;

        const collapsibleLoginProps = {
            facebookLoginUrl,
            fakeImportLoginForm,
            forceOpen: defaultOpenCollapsibleLogin,
            forceOpenCollapsibleLogin: defaultOpenCollapsibleLogin,
            googleLoginUrl,
            appleLoginUrl,
            loginReturnUrl,
            recordRememberMeAssignment,
            showLoginRememberMe,
            splitLoginAndSignupLink,
            useCollapsibleLogin: useLoginModal,
        };

        const mobileMenu = (
            <Provider store={this.props.store}>
                <StyledMobileMenu
                    {...collapsibleLoginProps}
                    data-test="header-mobile-menu"
                    mobileMenuTriggerDataTest={mobileMenuTriggerDataTest}
                    onBeforeLinkClick={onMobileMenuLinkClick}
                    usePatientHomeView={usePatientHomeView}
                    wellGuideLinkVariant={wellGuideLinkVariant}
                >
                    {mobileMenuContent}
                </StyledMobileMenu>
            </Provider>
        );

        return (
            <ThemeProvider
                theme={{
                    transparentOnDesktop,
                    transparentOnAll,
                    usePatientHomeV3Variant,
                }}
            >
                <Metrics section="PageHeader">
                    <div role="banner">
                        {this.maybeRenderPrivacyUpdateBanner()}
                        {this.maybeRenderCoronaVirusBanner()}
                        <StyledContainer
                            data-test="header"
                            data-uem-id="header"
                            {...filterToValidAttributes(props)}
                        >
                            <div>
                                {this.maybeRenderPoweredByZocdoc()}
                                <OurHeaderContent
                                    data-test="header-content"
                                    shouldHideLogoForDesktop={
                                        this.props.shouldHideLogoForDesktop
                                    }
                                >
                                    {!rightAlignMobileMenu &&
                                        !hideUserArea &&
                                        mobileMenu}
                                    {this.maybeRenderStyledLogo()}
                                    {inlineChildren}
                                    {this.maybeRenderUserArea()}
                                    {rightAlignMobileMenu &&
                                        !hideUserArea &&
                                        mobileMenu}
                                </OurHeaderContent>
                            </div>
                            {children}
                        </StyledContainer>
                        <BottomBorder />
                    </div>
                </Metrics>
            </ThemeProvider>
        );
    }

    renderCustomContent = (definition, index) => {
        if (definition.uri) {
            let metricsData = {};

            const isSignIn = definition.uri.indexOf('signin') !== -1;
            const isJoin = definition.uri.indexOf('join') !== -1;

            if (isSignIn) {
                metricsData = {
                    action: 'Click Page Header Sign In Link',
                    element: 'SignInLink',
                };
            } else if (isJoin) {
                metricsData = {
                    action: 'Click Page Header Join Link',
                    element: 'JoinLink',
                };
            }

            return (
                <MetricsClickable {...metricsData} key={index}>
                    <StyledLink
                        key={index}
                        data-test="header-custom-content-link"
                        href={definition.uri}
                        onClick={this.props.onCustomLinkClick}
                        title={definition.title}
                    >
                        {definition.text}
                    </StyledLink>
                </MetricsClickable>
            );
        } else {
            return (
                <span key={index} data-test="header-custom-content-text">
                    {definition.text}
                </span>
            );
        }
    };

    maybeRenderCustomContent(showLoginDropdown, content) {
        const {
            defaultShowLoginModal,
            dropdownMenuDataTest,
            facebookLoginUrl,
            fakeImportLoginModal,
            googleLoginUrl,
            appleLoginUrl,
            onSignInDropdownClick,
            onSignInDropdownLinkClick,
            recordRememberMeAssignment,
            loginReturnUrl: returnUrl,
            showLoginRememberMe: showRememberMe,
            splitLoginAndSignupLink,
            trackSignInLinkClickConversion,
            useLoginModal,
            useSignInDropdown,
        } = this.props;

        if (useSignInDropdown && showLoginDropdown) {
            const signInDropdownProps = {
                defaultShowLoginModal,
                dropdownMenuDataTest,
                facebookLoginUrl,
                fakeImportLoginModal,
                googleLoginUrl,
                appleLoginUrl,
                onSignInDropdownClick,
                onSignInDropdownLinkClick,
                recordRememberMeAssignment,
                returnUrl,
                showRememberMe,
                splitLoginAndSignupLink,
                trackSignInLinkClickConversion,
                useLoginModal,
            };
            return (
                <SignInDropdownContainer data-test="signin-dropdown-container">
                    <SignInDropdown
                        displayName={this.getDropDownText()}
                        {...signInDropdownProps}
                    />
                </SignInDropdownContainer>
            );
        }

        if (content) {
            return (
                <Metrics component="RightContentCustomLink">
                    <StyledLinkGroup data-test="header-custom-content">
                        {content.map(this.renderCustomContent)}
                    </StyledLinkGroup>
                </Metrics>
            );
        }
    }

    getDropDownText() {
        return this.props.signInDropdownText || 'Log in / Sign up';
    }

    maybeRenderCTA() {
        const {
            theme: { directoryId },
            onCTALinkClick,
            shouldShowNewSearchHeader,
        } = this.props;

        if (isZocdocBrand(directoryId)) {
            const displayedText = shouldShowNewSearchHeader
                ? 'List your practice'
                : 'List your practice on Zocdoc';
            return (
                <StyledCTALinkGroup>
                    <StyledCTALinkContainer
                        hideBorder={shouldShowNewSearchHeader}
                    >
                        <MetricsClickable
                            action="Click PageHeader CTA Link"
                            component="CTALink"
                            element="ListYourPractice"
                        >
                            <StyledCTALink
                                beforeClick={onCTALinkClick}
                                data-test="header-cta"
                                href="/join?utm_medium=organicpro&utm_source=website&utm_campaign=PatientSite_Header"
                            >
                                {displayedText}
                            </StyledCTALink>
                        </MetricsClickable>
                    </StyledCTALinkContainer>
                </StyledCTALinkGroup>
            );
        }
    }

    maybeRenderPoweredByZocdoc() {
        const {
            theme: { header },
            user,
        } = this.props;

        const loginState = this.getLoginState(user);
        if (
            header.showPoweredByZocdoc &&
            loginState === LOGIN_STATE.LoggedOut
        ) {
            return (
                <StyledPoweredByZocdoc
                    component={
                        header.showPoweredByZocdocAsLink ? 'a' : undefined
                    }
                    data-test="header-powered_by_zocdoc"
                />
            );
        }
    }

    getLoginState(user) {
        return get(user, 'login_state', LOGIN_STATE.LoggedOut);
    }

    maybeRenderUserArea() {
        const {
            hideUserArea,
            onUsernameDropdownClick,
            onUsernameDropdownLinkClick,
            showNavigationLinks,
            showSubPatientDropdownLink,
            numberOfNavLinksToShowInUserDropdown,
            shouldShowNewSearchHeader,
            theme: { header },
            theme,
            useAnimatedLogo,
            usePatientHomeV3Variant,
            user,
        } = this.props;

        if (hideUserArea) {
            return null;
        }

        const loginState = this.getLoginState(user);
        if (loginState === LOGIN_STATE.LoggedOut) {
            const rightContent = get(theme, 'header.rightContent');

            return (
                <Provider store={this.props.store}>
                    <CtaContent data-test="cta-content">
                        {this.maybeRenderCTA()}
                        {this.maybeRenderCustomContent(
                            header.showLoginDropdown,
                            rightContent
                        )}
                    </CtaContent>
                </Provider>
            );
        } else if (
            loginState === LOGIN_STATE.SoftLoggedIn ||
            loginState === LOGIN_STATE.LoggedIn
        ) {
            const displayName = user.display_name || user.email;
            return (
                <Provider store={this.props.store}>
                    <Metrics component="RightContentCustomLink">
                        <UserHeader
                            data-test="user-menu"
                            useAnimatedLogo={useAnimatedLogo}
                        >
                            {this.maybeRenderNavigationLinks(loginState)}
                            {this.maybeRenderNotificationMenu()}

                            <UsernameDropdown
                                displayName={displayName}
                                onUsernameBarClick={onUsernameDropdownClick}
                                onUsernameDropdownLinkClick={
                                    onUsernameDropdownLinkClick
                                }
                                usePatientNavigationLinks={
                                    showNavigationLinks ||
                                    usePatientHomeV3Variant
                                }
                                useSubPatientDropdownLink={
                                    showSubPatientDropdownLink
                                }
                                userType={user.user_type}
                                numberOfNavLinksToShowInUserDropdown={
                                    numberOfNavLinksToShowInUserDropdown
                                }
                                shouldShowNewSearchHeader={
                                    shouldShowNewSearchHeader
                                }
                                usePatientHomeV3Variant={
                                    usePatientHomeV3Variant
                                }
                            />
                        </UserHeader>
                    </Metrics>
                </Provider>
            );
        }
    }

    maybeRenderNavigationLinks(userLoginState) {
        const { user } = this.props;

        if (user.user_type === USER_TYPE.Patient) {
            return this.maybeRenderPatientNavigationLinks(userLoginState);
        } else {
            return this.maybeRenderProviderNavigationLinks(userLoginState);
        }
    }

    shouldRenderPatientNavigationLinks(userLoginState) {
        const { showNavigationLinks, usePatientHomeV3Variant } = this.props;

        return (
            (showNavigationLinks || usePatientHomeV3Variant) &&
            (userLoginState === LOGIN_STATE.LoggedIn ||
                userLoginState === LOGIN_STATE.SoftLoggedIn)
        );
    }

    maybeRenderPatientNavigationLinks(userLoginState) {
        if (this.shouldRenderPatientNavigationLinks(userLoginState)) {
            const { numberOfNavLinksToShowInUserDropdown } = this.props;
            const navLinksToShowInHeader =
                DesktopUserNavLinks.patient.length -
                numberOfNavLinksToShowInUserDropdown;
            return this.renderNavigationLinks(
                DesktopUserNavLinks.patient.slice(0, navLinksToShowInHeader)
            );
        }
    }

    maybeRenderProviderNavigationLinks(userLoginState) {
        if (userLoginState === LOGIN_STATE.LoggedIn) {
            return this.renderNavigationLinks(DesktopUserNavLinks.Provider);
        }
    }

    renderNavigationLinks(userNavLinks) {
        return (
            <NavLinkGroup data-test="header-nav-group">
                {userNavLinks.map(navLink => {
                    return this.renderNavigationLink(
                        navLink,
                        this.props.onNavigationLinkClick
                    );
                })}
            </NavLinkGroup>
        );
    }

    renderNavigationLink(navLink, onNavigationLinkClick) {
        const key = navLink.dataTest;
        const selected = this.getPathNameFromWindowLocation() === navLink.url;

        return (
            <NavLinkDiv key={key}>
                <StyledLink
                    beforeClick={ev => onNavigationLinkClick(ev, key)}
                    data-test={`header-nav-links-${key}`}
                    data-uem-id={`header-nav-links-${key}`}
                    href={navLink.url}
                    selected={selected}
                >
                    {navLink.message}
                </StyledLink>
            </NavLinkDiv>
        );
    }

    getPathNameFromWindowLocation() {
        const { __windowLocationPathName__ } = this.props;
        const pathName = __windowLocationPathName__ || window.location.pathname;
        return pathName ? pathName.trim() : null;
    }

    maybeRenderNotificationMenu() {
        const {
            onNotificationClick,
            onNotificationDropdownClick,
            onSeeAllNotificationsClick,
            onUpcomingAppointmentClick,
            user,
            shouldShowNewSearchHeader,
            usePatientHomeV3Variant,
        } = this.props;

        if (user.user_type === USER_TYPE.Patient) {
            return (
                <NotificationMenu
                    data-test="header-notification-menu"
                    onNotificationClick={onNotificationClick}
                    onNotificationDropdownClick={onNotificationDropdownClick}
                    onSeeAllNotificationsClick={onSeeAllNotificationsClick}
                    onUpcomingAppointmentClick={onUpcomingAppointmentClick}
                    shouldShowNewSearchHeader={shouldShowNewSearchHeader}
                    usePatientHomeV3Variant={usePatientHomeV3Variant}
                />
            );
        }
    }

    maybeRenderPrivacyUpdateBanner() {
        const { ab } = this.props;
        const featureFlag = get(
            ab,
            UPDATE_PRIVACY_POLICY_BANNER_FLAG.experimentId,
            VARIANT_HIDDEN
        );

        if (featureFlag === VARIANT_SHOWN) {
            return <PrivacyUpdateBanner />;
        }
    }

    //In the future, consider replacing this with a generic banner that can dymanically get the AlertBanner data from somewhere (redux?)
    //For now, handle it like the privacy update banner.
    maybeRenderCoronaVirusBanner() {
        const {
            shouldShowCoronaVirusBanner,
            onClickCoronaVirusBanner,
        } = this.props;

        if (shouldShowCoronaVirusBanner) {
            return (
                <Metrics
                    section="PageHeader"
                    action="Click Corona Virus Alert Banner"
                    element="CoronaVirusAlertBanner"
                    component="CoronaVirusAlertBanner"
                >
                    <CoronaVirusAlertBanner
                        onClick={onClickCoronaVirusBanner}
                    />
                </Metrics>
            );
        }
    }

    renderLogo() {
        const {
            onLogoClick,
            theme: { header },
            transparentOnDesktop,
            transparentOnAll,
            useAnimatedLogo,
            usePatientHomeV3Variant,
            useStaticLargeBlobLogo,
            useStaticSmallBlobLogo,
        } = this.props;

        if (
            useStaticSmallBlobLogo ||
            useStaticLargeBlobLogo ||
            usePatientHomeV3Variant
        ) {
            return (
                <Logo>
                    <MetricsClickable>
                        {useStaticSmallBlobLogo || usePatientHomeV3Variant ? (
                            <StaticSmallBlobLogo
                                beforeClick={onLogoClick}
                                data-test="static-small-blob-header-logo"
                                data-uem-id="static-small-blob-header-logo"
                                href={header.logo.linkUri}
                                title="Zocdoc"
                            />
                        ) : (
                            <StaticLargeBlobLogo
                                beforeClick={onLogoClick}
                                data-test="static-large-blob-header-logo"
                                data-uem-id="static-large-blob-header-logo"
                                href={header.logo.linkUri}
                                title="Zocdoc"
                            />
                        )}
                    </MetricsClickable>
                </Logo>
            );
        } else {
            const LogoComponent =
                transparentOnDesktop ||
                transparentOnAll ||
                usePatientHomeV3Variant
                    ? TransparentStaticLogo
                    : StaticLogo;

            return (
                <Logo>
                    <MetricsClickable>
                        {useAnimatedLogo && (
                            <AnimatedLogo
                                beforeClick={onLogoClick}
                                data-test="animated-header-logo"
                                href={header.logo.linkUri}
                                ref={node => {
                                    this.$animatedLogoContainer = findDOMNode(
                                        node
                                    );
                                }}
                                title="Back to homepage"
                            />
                        )}

                        <LogoComponent
                            beforeClick={onLogoClick}
                            data-test="static-header-logo"
                            data-uem-id="static-header-logo"
                            href={header.logo.linkUri}
                            title="Back to homepage"
                        />
                    </MetricsClickable>
                </Logo>
            );
        }
    }

    maybeRenderAnimation = () => {
        if (
            this.props.useAnimatedLogo &&
            !this.animationRendered &&
            process.env.NODE_ENV !== 'test' &&
            !isMobileOrTabletBreakpoint()
        ) {
            this.renderAnimation();
        }
    };

    renderStyledLogo() {
        return (
            <StyledLogo
                data-test="styled-logo"
                hiddenForMobile={this.props.shouldHideLogoForMobile}
                hiddenForDesktop={this.props.shouldHideLogoForDesktop}
            >
                <Metrics
                    action="Click PageHeader Logo"
                    component="Logo"
                    element="Logo"
                >
                    {this.renderLogo()}
                </Metrics>
            </StyledLogo>
        );
    }

    maybeRenderStyledLogo() {
        return this.renderStyledLogo();
    }

    async renderAnimation() {
        this.animationRendered = true;

        const [lottie, json] = await Promise.all([
            import(
                /* webpackChunkName: "header-animation" */ 'lottie-web/build/player/lottie_light.min.js'
            ),
            import(
                /* webpackChunkName: "header-animation" */ './AnimatedLogo.json'
            ),
        ]);

        const animationParams = {
            container: this.$animatedLogoContainer,
            renderer: 'svg',
            loop: false,
            autoplay: true,
            animationData: json,
        };

        // enzyme cannot run with real dom manipulations from lottie
        lottie.loadAnimation(animationParams);
    }
}

/* region Styles */

const themeHeader = props => props.theme.header;

const hideForMobile = css`
    ${breakpoint('small', { down: true })} {
        display: none;
    }
`;

const StyledMobileMenu = styled(MobileMenu)`
    z-index: 10;

    ${breakpoint.medium} {
        display: none;
    }
`;

const BottomBorder = styled.div`
    background: ${p => p.theme.header.bottomBorderColor};
    display: ${p =>
        p.theme.header.bottomBorderColor === 'transparent' ? 'none' : 'block'};
    height: 1px;

    ${breakpoint.medium} {
        background: ${p =>
            p.theme.transparentOnAll || p.theme.transparentOnDesktop
                ? 'transparent'
                : p.theme.header.bottomBorderColor};
    }

    background: ${p =>
        p.theme.transparentOnAll ? 'none' : p.theme.header.bgColor};

    ${p => {
        if (p.theme.usePatientHomeV3Variant) {
            return `
                    background: ${PATIENT_HOME_V3_BACKGROUND_COLOR};
                        ${breakpoint.medium} {
                            background: transparent;
                        }
                `;
        }
    }}
`;

const StyledContainer = styled(PaddedContainer)`
    background: ${p => p.theme.header.bgColor};
    box-sizing: border-box;
    color: ${p => themeHeader(p).textColor};
    font-family: ${FontFamily.SharpSansSemibold};
    font-size: 14px;
    padding-bottom: ${HEADER_Y_AXIS_PADDING}px;
    padding-top: ${HEADER_Y_AXIS_PADDING}px;
    position: relative;

    *,
    *::before,
    *::after {
        box-sizing: border-box;
    }

    ${breakpoint.medium} {
        background: ${p =>
            p.theme.transparentOnDesktop || p.theme.transparentOnAll
                ? 'none'
                : p.theme.header.bgColor};
    }

    background: ${p =>
        p.theme.transparentOnAll ? 'none' : p.theme.header.bgColor};

    ${p => {
        if (p.theme.usePatientHomeV3Variant) {
            return `
                background: ${PATIENT_HOME_V3_BACKGROUND_COLOR};
                    ${breakpoint.medium} {
                        background: transparent;
                    }
            `;
        }
    }}
`;

const OurHeaderContent = styled.div`
    position: relative;
    align-items: center;
    display: flex;

    ${breakpoint.medium} {
        ${props =>
            props.shouldHideLogoForDesktop &&
            css`
                justify-content: flex-end;
            `}
    }
`;

const CtaContent = styled.div`
    align-self: flex-start;
    align-items: center;
    display: flex;
    ${breakpoint.medium} {
        align-self: ${p =>
            themeHeader(p).showPoweredByZocdoc ? 'flex-end' : 'flex-start'};
    }
`;

const UserHeader = styled.div`
    align-self: ${p => (p.useAnimatedLogo ? 'flex-start' : 'inherit')};
    display: none;
    flex-grow: 1;
    justify-content: flex-end;

    ${breakpoint.medium} {
        display: flex;
    }
`;

// No styles here, but consumers might style it
const Logo = styled.div``;

const StaticLogo = styled(LockableLink)`
    background-image: url("${p => getLogo(p.theme).uri}");
    background-position: center left;
    background-repeat: no-repeat;
    background-size: contain;
    display: block;

    ${p => getLogoSizingCSSAtBreakpoint(p.theme, 'xxsmall')}
    ${p => getLogoSizingCSSAtBreakpoint(p.theme, 'xsmall')}
    ${p => getLogoSizingCSSAtBreakpoint(p.theme, 'small')}
    ${p => getLogoSizingCSSAtBreakpoint(p.theme, 'medium')}
    ${p => getLogoSizingCSSAtBreakpoint(p.theme, 'large')}
    ${p => getLogoSizingCSSAtBreakpoint(p.theme, 'xlarge')}
    ${p => getLogoSizingCSSAtBreakpoint(p.theme, 'xxlarge')}
`;

const getLogoUri = logo => logo.transparentUri || logo.uri;
const TransparentStaticLogo = styled(StaticLogo)`
    ${breakpoint.medium} {
        background-image: url("${p => getLogoUri(getLogo(p.theme))}");
    }
`;

const AnimatedLogo = styled(Link)`
    border: none !important;
    display: none;
    position: absolute;
    top: 0;
    left: 0;

    ${breakpoint.medium} {
        display: block;

        & ~ ${StaticLogo}, & ~ ${TransparentStaticLogo} {
            display: none;
        }
    }
`;

const StaticSmallBlobLogo = styled(LockableLink)`
    background-image: url(${StaticSmallBlobLogoImage});
    background-position: center left;
    background-repeat: no-repeat;
    background-size: contain;
    display: block;
    height: 44px;
    width: 154px;
`;

const StaticLargeBlobLogo = styled(LockableLink)`
    background-image: url(${StaticLargeBlobLogoImage});
    background-position: center left;
    background-repeat: no-repeat;
    background-size: contain;
    display: block;
    height: 46px;
    width: 123px;

    ${breakpoint.medium} {
        height: 58px;
        width: 154px;
    }
`;

const StyledPoweredByZocdoc = styled(PoweredByZocdoc)`
    border: 1px solid ${grey25};
    border-top: 0;
    color: ${p => themeHeader(p).textColor};
    padding: 7px 10px;
    width: 120px;
    position: absolute;
    top: 0;
    right: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.XXSMALL}px;

    ${breakpoint('small', { down: true })} {
        display: none;
    }

    ${breakpoint.small} {
        right: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.SMALL}px;
    }

    ${breakpoint.medium} {
        right: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.MEDIUM}px;
    }

    ${breakpoint.large} {
        right: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.LARGE}px;
    }

    ${breakpoint.xlarge} {
        right: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.XLARGE}px;
    }

    ${breakpoint.xxlarge} {
        right: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.XXLARGE}px;
    }
`;

const StyledLink = styled(Link)`
    border-bottom-color: ${p => themeHeader(p).linkUnderlineColor};
    ${p => (p.selected ? 'border-bottom-style: solid' : '')};
    ${p => (p.selected ? `font-family: ${FontFamily.SharpSansBold}` : '')};
    color: ${p => themeHeader(p).linkColor};
    display: inline-block;
    &:focus,
    &:hover {
        border-bottom-color: ${p => themeHeader(p).linkUnderlineColorHover};
        color: ${p => themeHeader(p).linkColorHover};
    }
    &:visited {
        border-bottom-color: ${p => themeHeader(p).linkUnderlineColorVisited};
        color: ${p => themeHeader(p).linkColorVisited};
    }
`;

const StyledCTALink = styled(Link)`
    color: ${p => themeHeader(p).linkColor};
    font-size: 14px;
    display: inline-block;
    ${hideForMobile}
`;
const StyledCTALinkContainer = styled.div`
    border-right: 1px solid ${p => themeHeader(p).ctaLinkColor};
    padding: 0 20px;

    ${p =>
        p.hideBorder &&
        css`
            border-right: none;
        `}

    ${hideForMobile}
`;
const StyledCTALinkGroup = styled.div`
    ${breakpoint.medium} {
        padding: 13px 0 12px 0;
    }
`;
const StyledLinkGroup = styled.div`
    margin-left: 21px;

    ${hideForMobile}

    > *:not(:first-child) {
        margin-left: 4px;
    }
`;

const NavLinkGroup = styled.div`
    display: inline-flex;
    flex-wrap: nowrap;
    margin: 10px 10px;
`;

const NavLinkDiv = styled.div`
    margin-right: 25px;
`;

const SignInDropdownContainer = styled.div`
    margin-left: 20px;
    ${hideForMobile}
`;

const StyledLogo = styled.div`
    display: inline-block;
    margin-right: auto;

    ${breakpoint('small', { down: true })} {
        display: ${p => (p.hiddenForMobile ? 'none' : 'inline-block')};
    }

    ${breakpoint.medium} {
        display: ${p => (p.hiddenForDesktop ? 'none' : 'inline-block')};
    }
`;

/* endregion Styles */

export default withTheme(Header);
export const primitives = {
    AnimatedLogo,
    BottomBorder,
    CTALink: StyledCTALink,
    Container: StyledContainer,
    CtaContent,
    Link: StyledLink,
    LinkGroup: StyledLinkGroup,
    Logo,
    MobileMenu: StyledMobileMenu,
    PoweredByZocdoc: StyledPoweredByZocdoc,
    StaticLogo,
    StyledLogo,
    TransparentStaticLogo,
    UserHeader,
    ZDHeaderContent: OurHeaderContent,
};
